import React, { useEffect, useState, useMemo } from 'react';
import ErrorLogTable from '../components/tables/communicate-page/ErrorLogTable';
import BatteryLogTable from '../components/tables/communicate-page/BatteryLogTable';
import WiFiSettingsCard from '../components/WifiSettingsCard';
import { ButtonPrimary, ButtonSecondary } from '../components/Buttons';
import { FileDropzone, SelectInput, TextareaInput } from '../components/Inputs';
import { BarChart } from '../components/Charts';
import GoogleMapsComponent from '../components/GoogleMapsComponent';
import SequentialCommandsModal from '../components/modals/communicate_page_modals/SequentialCommandsModal';
import DownloadFilesTable from '../components/tables/communicate-page/DowloadFilesTable';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useAsyncError, useParams } from 'react-router-dom';
import UnitOccupiedModal from '../components/modals/communicate_page_modals/UnitOccupiedModal';
import ActivityLog from '../components/ActivityLog';
import CommandSchedulingModal from '../components/modals/communicate_page_modals/CommandSchedulingModal';

const CommunicatePage = () => {
  const [sequentialGuideModalShowing, setSequentialGuideModalShowing] =
    useState(false);
  const [commandSchedulingModalShowing, setCommandSchedulingModalShowing] =
    useState(false);
  const [sequentialCommands, setSequentialCommands] = useState('');
  const [unitOccupiedModalShowing, setUnitOccupiedModalShowing] =
    useState(false);
  const [curOccupant, setCurOccupant] = useState(null);
  
  const [lastRsvp, setLastRsvp] = useState({});
  const [wifiConnections, setWifiConnections] = useState({});
  const [uploadFilesToS3, setUploadFilesToS3] = useState(false);
  const [availableFilenames, setAvailableFilenames] = useState([]);
  const [googleMapRendered, setGoogleMapRendered] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [cycleTimes, setCycleTimes] = useState([]);
  const [rsvpLogPeriodSelected, setRsvpLogPeriodSelected] = useState(86400);
  const [errorLogs, setErrorLogs] = useState([]);
  const [batteryLogs, setBatteryLogs] = useState([]);
  const [markerPosition, setMarkerPosition] = useState({ lat: 0, lng: 0 });
  const [sequentialCommandsInDevice, setSequentialCommandsInDevice] =
    useState('');
  const {
    sendAuthenticatedRequest,
    currentUser,
    uploadAuthenticatedFile,
    getAuthenticatedFile,
  } = useAuth();
  const { serialNumber } = useParams();

  let rsvpErrorWasShown = false;

  const barchartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Frequency',
        },
        beginAtZero: true,
      },
    },
  };

  const checkConcurrency = async () => {
    try {
      const response = await sendAuthenticatedRequest(
        `/concurrency/${serialNumber}`
      );
      if (response.occupied && response.cur_occupant !== currentUser?.email) {
        setUnitOccupiedModalShowing(true);
        setCurOccupant(response.cur_occupant);
      } else {
        // Occupy the unit if not currently occuped
        sendAuthenticatedRequest(`/concurrency/${serialNumber}`, 'POST');
        setCurOccupant(currentUser?.email);
      }
    } catch (e) {
      toast.error(
        'Error checking concurrency. You may be in read-only mode - refreshing the page is recommended'
      );
      console.error(e);
    }
  };

  const updateConcurrency = async () => {
    if (curOccupant === currentUser?.email) {
      sendAuthenticatedRequest(`/concurrency/${serialNumber}`, 'POST');
    }
  };

  const loadDeviceInfo = async () => {
    try {
      const info = await sendAuthenticatedRequest(`/devices/${serialNumber}`);

      // Parse the location if available
      if (info?.lastloc) {
        const [latPart, lngPart] = info.lastloc.split(',');

        // Extract latitude and longitude with N/S and E/W
        const latDirection = latPart.slice(-1); // N/S
        const lngDirection = lngPart.slice(-1); // E/W

        let lat = parseFloat(latPart.slice(0, -1)); // Remove the last character (N/S)
        let lng = parseFloat(lngPart.slice(0, -1)); // Remove the last character (E/W)

        // Adjust for direction
        if (latDirection === 'S') lat = -lat;
        if (lngDirection === 'W') lng = -lng;

        setMarkerPosition({ lat, lng });
      } else {
        setMarkerPosition({ lat: 0, lng: 0 }); // Default to 0, 0 if no location
      }

      setDeviceInfo(info); // Store device info in state
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch device info');
    }
  };

  

  const loadErrorLogs = async () => {
    try {
      setErrorLogs(
        await sendAuthenticatedRequest(
          `/devices/${serialNumber}/logs/error?limit=10`
        )
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch error logs');
    }
  };

  const loadBatteryLogs = async () => {
    try {
      setBatteryLogs(
        await sendAuthenticatedRequest(
          `/devices/${serialNumber}/logs/battery?limit=10`
        )
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch battery logs');
    }
  };

  const loadCycleStopTimes = async () => {
    try {
      const cycleTimes = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/logs/logao1/cycle-stop-times`
      );
      setCycleTimes(cycleTimes);
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch cycle stop times');
    }
  };

  const loadRsvp = async () => {
    try {
      const rsvp = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/rsvp`
      );
      setLastRsvp(rsvp);
    } catch (e) {
      console.error(e);
      if (!rsvpErrorWasShown) {
        toast.error('Failed to fetch last RSVP');
        rsvpErrorWasShown = true;
      }
    }
  };

  const loadWifiConnections = async () => {
    try {
      setWifiConnections(
        await sendAuthenticatedRequest(`/devices/${serialNumber}/connections`)
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch WiFi connections');
    }
  };

  const loadAvailableFilenames = async () => {
    try {
      setAvailableFilenames(
        await sendAuthenticatedRequest(`/devices/${serialNumber}/update-files`)
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch update files');
    }
  };

  const loadSequentialCommandsInDevice = async () => {
    try {
      const resp = await sendAuthenticatedRequest(
        `/commands/${serialNumber}?as-string=true`
      );
      setSequentialCommandsInDevice(resp['commands']);
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch sequential commands');
    }
  };

  const cycleData = useMemo(() => {
    // If no cycle data, return a dummy data structure for visual purposes
    if (cycleTimes.length === 0) {
      return {
        labels: Array.from({ length: 24 }, (_, i) => i.toString()),
        datasets: [
          {
            label: 'Cycle Times',
            data: Array(24).fill(0), // Dummy data
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
          },
        ],
      };
    }

    // Normal data processing for cycleTimes
    const hours = Array(24).fill(0);
    cycleTimes.forEach((timestamp) => {
      const date = new Date(timestamp);
      const hour = date.getHours();
      hours[hour]++;
    });

    return {
      labels: Array.from({ length: 24 }, (_, i) => i.toString()),
      datasets: [
        {
          label: 'Cycle Times',
          data: hours,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [cycleTimes]);

  useEffect(() => {
    checkConcurrency();
    loadRsvp();
    loadWifiConnections();
    loadAvailableFilenames();
    loadCycleStopTimes();
    loadDeviceInfo();
    loadErrorLogs();
    loadBatteryLogs();
    loadSequentialCommandsInDevice();
  }, []);

  useEffect(() => {
    const interval = setInterval(loadRsvp, 1000);
    return () => clearInterval(interval);
  }, [serialNumber]);

  useEffect(() => {
    // Set up the interval to run every 5 minutes (300,000 milliseconds)
    const intervalId = setInterval(updateConcurrency, 300000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [curOccupant, currentUser, serialNumber]);

  ///////////////////////////
  // Click Handlers
  ///////////////////////////

  // Wifi Settings

  const handleWifiCredsChanged = (wifiName, field, value) => {
    setWifiConnections((prevConnections) => ({
      ...prevConnections,
      [wifiName]: {
        ...prevConnections[wifiName],
        [field]: value,
      },
    }));
  };

  const handleSaveWifi = async (wifiName) => {
    try {
      const settings = wifiConnections[wifiName];

      const data = {
        username: settings.username,
        password: settings.pw,
        address: settings.address,
        id: wifiName.split('_')[1],
      };

      const resp = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/connections`,
        'PATCH',
        data
      );
      setWifiConnections(resp);
      toast.success('Credentials changed successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong saving the wifi credentials.');
    }
  };

  const handleReturnHome = () => {
    sendAuthenticatedRequest(`/concurrency/${serialNumber}/relinquish`, 'POST');
    window.location.href = '/';
  };

  const handleUploadFile = async (file) => {
    try {
      const resp = await uploadAuthenticatedFile(
        `/devices/${serialNumber}/update-files`,
        file,
        {
          upload_to_aws: uploadFilesToS3 ? 'y' : 'n',
        }
      );
      if (!resp.error) {
        toast.success('Uploaded file successfully!');
      } else {
        toast.error('Something went wrong uploading the file');
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong uploading the file');
    }
  };

  const handleSetSequentialCommands = async () => {
    try {
      await sendAuthenticatedRequest(`/commands/${serialNumber}`, 'POST', {
        commands: sequentialCommands.split('\n'), // Use the correct delimiter
      });
      toast.success('Commands set successfully!');
      setSequentialCommands('');
      loadSequentialCommandsInDevice();
    } catch (e) {
      console.error(e);

      // Check if error contains response data
      if (e.response) {
        toast.error(e.response.error || 'Something went wrong with the API.');
      } else {
        toast.error('Something went wrong setting commands.');
      }
    }
  };

  // In the command scheduler modal
  const handleScheduleCommands = async (block) => {
    try {
      await sendAuthenticatedRequest(`/commands/${serialNumber}`, 'POST', {
        commands: block.split('\n'), // Use the correct delimiter
      });
      toast.success('Successfully scheduled commands!');
    } catch (e) {
      console.error(e);
      if (e.response) {
        toast.error(e.response.error || 'Something went wrong with the API.');
      } else {
        toast.error('Something went wrong scheduling commands.');
      }
    }
  };

  const downloadRsvpLogs = async () => {
    try {
      const rsvps = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/logs/rsvp?period=${rsvpLogPeriodSelected}`
      );

      const strip = (v) => String(v).replace(/\r?\n|\r/g, ' ');

      const csvRows = [
        ['Time', 'RSVP'],
        ...Object.entries(rsvps).map(([ts, rsvp]) => {
          const time = new Date(ts * 1000).toISOString().replace('T', ' ').replace('Z', '');
          return [strip(time), strip(rsvp)];
        })
      ];

      const csvContent = csvRows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = Object.assign(document.createElement('a'), {
        download: 'data.csv',
        href: URL.createObjectURL(blob)
      });

      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch RSVP logs');
    }
  };


  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      <SequentialCommandsModal
        isOpen={sequentialGuideModalShowing}
        setIsOpen={setSequentialGuideModalShowing}
        startPosition={{ top: 100, right: 20 }}
        onAddCommandToCodeClicked={(usageString) =>
          setSequentialCommands(
            sequentialCommands == ''
              ? usageString
              : `${sequentialCommands}\n${usageString}`
          )
        }
      />
      <CommandSchedulingModal
        isOpen={commandSchedulingModalShowing}
        setIsOpen={setCommandSchedulingModalShowing}
        startPosition={{ top: 100, left: 20 }}
        onScheduleButtonClicked={handleScheduleCommands}
      />

      <UnitOccupiedModal
        isOpen={unitOccupiedModalShowing}
        onClose={() => setUnitOccupiedModalShowing(false)}
        serialNumber={serialNumber}
        userEmail={curOccupant}
      />

      
        <div className="sticky top-0 z-40 bg-background py-4">
          <div className="flex flex-col w-full">
            <div className="flex flex-row w-full justify-between mb-2">
              <h1 className="text-3xl font-bold text-black">{`Communicate with ${serialNumber}`}</h1>
              <ButtonSecondary onClick={handleReturnHome}>
                Return home
              </ButtonSecondary>
            </div>
            <span className="italic text-lg text-accent">
              Last RSVP: <span className="font-bold">{lastRsvp.message}</span>. (Received{' '}
              <span className="font-bold">
                {Math.floor((Math.floor(Date.now() / 1000) - lastRsvp.time) / 60)}
              </span>{' '}
              mins ago).
            </span>
          </div>
        </div>
        {/* TODO: use navigate when everything is translated to react */}
        

      <div className="flex flex-row w-full space-x-4">
        {Object.entries(wifiConnections).map(([wifiName, settings]) => (
          <WiFiSettingsCard
            key={wifiName}
            wifiNumber={wifiName.split('_')[1]}
            ssid={settings.username}
            password={settings.pw}
            address={settings.address}
            onSave={() => handleSaveWifi(wifiName)}
            onChange={(field, value) =>
              handleWifiCredsChanged(wifiName, field, value)
            }
          />
        ))}
      </div>

      <div className="flex flex-row justify-between space-x-4 w-full">
        <div className="flex flex-col space-y-2 w-full">
          <h3 className="text-lg font-bold text-primary text-center">
            Upload update file
          </h3>
          <FileDropzone onChange={handleUploadFile} />

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={uploadFilesToS3}
              onChange={() => setUploadFilesToS3(!uploadFilesToS3)}
              className="form-checkbox h-5 w-5 text-primary"
            />
            <span className="text-secondary">
              Also upload files to S3 (use for update files)
            </span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-gray-300 w-px h-full"></div>
          </div>
          <div className="relative z-10 px-2 bg-background text-gray-500 font-semibold text-sm italic">
            or
          </div>
        </div>
        <div className="flex flex-col space-y-4 w-full">
          <DownloadFilesTable
            filenames={availableFilenames}
            serialNumber={serialNumber}
          />
          <div className="flex flex-row space-x-4">
            <SelectInput
              options={[
                { value: 86400, label: '1 Day' },
                { value: 604800, label: '7 Days' },
                { value: 2592000, label: '1 Month' },
                { value: 'all', label: 'All Time' }, // Special case for 'all'
              ]}
              label="Select Period"
              value={rsvpLogPeriodSelected}
              onChange={(e) => setRsvpLogPeriodSelected(e.target.value)}
              className="w-full"
            />
            <ButtonSecondary onClick={downloadRsvpLogs}>
              Download RSVP Logs
            </ButtonSecondary>
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-4">
        <div className="flex flex-col space-y-1">
          <h2 className="text-xl font-bold text-primary">
            Sequential Commands Inputter
          </h2>
          <p className="italic">
            Enter commands in sequence here, with line breaks in between
            commands. Note that commands are case-sensitive. Open the{' '}
            <span
              className="text-link cursor-pointer hover:font-semibold hover:underline"
              onClick={() => setSequentialGuideModalShowing(true)}
            >
              guide
            </span>{' '}
            for the commands list.
          </p>
          <p className="italic">
            Open up the{' '}
            <span
              className="text-link cursor-pointer hover:font-semibold hover:underline"
              onClick={() => setCommandSchedulingModalShowing(true)}
            >
              command scheduling modal
            </span>{' '}
            to insert scheduling blocks more easily
          </p>
        </div>
        <TextareaInput
          value={sequentialCommands}
          onChange={(e) => setSequentialCommands(e.target.value)}
          placeholder={'Write sequential commands here'}
        />
        <TextareaInput
          value={sequentialCommandsInDevice}
          disabled={true}
          placeholder={'No commands in the SBC'}
        />
        <ButtonPrimary onClick={handleSetSequentialCommands}>
          Set Commands
        </ButtonPrimary>
      </div>

      <div className="flex flex-col space-y-4">
        <h2 className="text-xl font-bold text-primary">
          Cycle Time Distributions
        </h2>

        <div className="relative w-full flex justify-center">
          {/* Blurred chart with text overlay */}
          {cycleTimes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <h2 className="text-2xl font-bold text-gray-600 bg-white bg-opacity-80 px-4 py-2 rounded-lg shadow-lg">
                No Cycle Data
              </h2>
            </div>
          )}

          <div className={cycleTimes.length === 0 ? 'filter blur-sm' : ''}>
            <BarChart
              data={cycleData} // Use the memoized cycle data here
              options={barchartOptions}
              style={{ height: '500px', width: '800px' }} // Adjust chart size for better layout
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-2">
        <h2 className="text-xl font-bold text-primary">Location</h2>
        {googleMapRendered ? (
          <GoogleMapsComponent
            containerStyle={{ width: '100%', height: '400px' }}
            markers={[
              {
                lat: markerPosition.lat,
                lng: markerPosition.lng,
              },
            ]}
          />
        ) : (
          <ButtonPrimary onClick={() => setGoogleMapRendered(true)}>
            Load Map
          </ButtonPrimary>
        )}
      </div>

      <div className="flex flex-col space-y-4">
        <h2 className="text-xl font-bold text-primary">
          Activity Logs
        </h2>
        <ActivityLog serialNumber={serialNumber} />
      </div>

      <div className="flex flex-col space-y-4">
        <h2 className="text-xl font-bold text-primary">Error Logs (Last 10)</h2>
        <ErrorLogTable logs={errorLogs} />
      </div>

      <div className="flex flex-col space-y-4">
        <h2 className="text-xl font-bold text-primary">
          Battery Logs (Last 10)
        </h2>
        <BatteryLogTable logs={batteryLogs} />
      </div>

      {/* Add other sections like Last Known Location, Waiting for Response, etc. */}
    </div>
  );
};

export default CommunicatePage;
